import React from "react";
import { Button, Space, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import LogFileNameCell from "./LogFileNameCell";
import { ProcessingStateTag, ReplicaTags, UploadStateTag } from "./logStateTags";
import { isReachable } from "../utils/logConstants";
import {
  formatBytes,
  formatCount,
  formatDuration,
  formatShortDateTime,
} from "../utils/formatters";

const MAX_INLINE_TASKS = 2;

/**
 * Build the columns for the MCAP log table.
 *
 * The grid carries EIGHT columns, each grouping the fields that belong
 * together; the composite ones stack a primary value and a muted secondary
 * line, so a row still shows robot + location, both timestamps, size +
 * duration, message + channel counts and upload + replica state without a
 * column per field. Everything that does not fit lives in the expandable row
 * (LogRowDetail), which reads from the same list response — no extra request.
 *
 * `responsive` progressively drops the lower-priority columns on narrow
 * viewports; File and Actions are always present. When the Robot column is the
 * one dropped, `compact` folds the robot id, size and upload time into the
 * File cell's secondary line so a phone row never loses them.
 *
 * Sorting is server-side (`sorter: true`); `sortBy`/`sortOrder` come from the
 * URL so the header arrow survives a reload or a shared link. A composite
 * column sorts on its primary field only — the key must be one the backend
 * accepts (SORT_FIELDS in utils/logConstants.js).
 */
export const createLogColumns = ({
  onView,
  onDownload,
  onDelete,
  sortBy,
  sortOrder,
  compact = false,
}) => {
  const sortStateFor = (key) => (sortBy === key ? sortOrder : null);

  return [
    {
      title: "File",
      dataIndex: "filename",
      key: "filename",
      sorter: true,
      sortOrder: sortStateFor("filename"),
      render: (filename, record) => (
        <div className="mcap-cell">
          <div className="mcap-cell__main">
            <LogFileNameCell filename={filename} />
          </div>
          {compact && (
            <div className="mcap-cell__sub">
              {record.robot_id ? `${record.robot_id} · ` : ""}
              {formatBytes(record.file_size)} ·{" "}
              {formatShortDateTime(record.uploaded_at)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Robot",
      dataIndex: "robot_id",
      key: "robot_id",
      width: 170,
      sorter: true,
      sortOrder: sortStateFor("robot_id"),
      responsive: ["sm"],
      render: (robotId, record) => (
        <div className="mcap-cell">
          <div className="mcap-cell__main">{robotId || "-"}</div>
          <div className="mcap-cell__sub">
            {record.location ? (
              <span>
                <EnvironmentOutlined /> {record.location}
              </span>
            ) : (
              "-"
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Tasks",
      dataIndex: "tasks",
      key: "tasks",
      width: 160,
      responsive: ["xl"],
      render: (tasks) => {
        const list = tasks || [];
        if (list.length === 0) return <span className="mcap-cell__sub">-</span>;
        const overflow = list.length - MAX_INLINE_TASKS;
        return (
          <div className="mcap-cell">
            <div className="mcap-cell__main">
              <Space size={2} wrap>
                {list.slice(0, MAX_INLINE_TASKS).map((task) => (
                  <Tag key={task} color="blue">
                    {task}
                  </Tag>
                ))}
                {overflow > 0 && (
                  // The full list is one hover away; the expandable row also
                  // carries it, so the cell never has to grow the row height.
                  <Tooltip title={list.slice(MAX_INLINE_TASKS).join(", ")}>
                    <Tag>+{overflow}</Tag>
                  </Tooltip>
                )}
              </Space>
            </div>
          </div>
        );
      },
    },
    {
      title: "Times",
      dataIndex: "recording_start_time",
      key: "recording_start_time",
      width: 180,
      sorter: true,
      sortOrder: sortStateFor("recording_start_time"),
      responsive: ["lg"],
      render: (recordedAt, record) => (
        <div className="mcap-cell">
          <Tooltip title="Recorded at">
            <div className="mcap-cell__main">
              {formatShortDateTime(recordedAt)}
            </div>
          </Tooltip>
          <Tooltip title="Uploaded at">
            <div className="mcap-cell__sub">
              ↑ {formatShortDateTime(record.uploaded_at)}
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Size",
      dataIndex: "file_size",
      key: "file_size",
      width: 130,
      sorter: true,
      sortOrder: sortStateFor("file_size"),
      responsive: ["xl"],
      render: (fileSize, record) => (
        <div className="mcap-cell">
          <div className="mcap-cell__main">{formatBytes(fileSize)}</div>
          <div className="mcap-cell__sub">
            {formatDuration(record.duration_seconds)}
          </div>
        </div>
      ),
    },
    {
      title: "Topics",
      dataIndex: "message_count",
      key: "message_count",
      width: 130,
      sorter: true,
      sortOrder: sortStateFor("message_count"),
      responsive: ["xxl"],
      render: (messageCount, record) => (
        <div className="mcap-cell">
          <div className="mcap-cell__main">
            {formatCount(messageCount)} msgs
          </div>
          <div className="mcap-cell__sub">
            {record.channel_count || 0} channels
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "upload_state",
      key: "status",
      width: 210,
      responsive: ["md"],
      render: (uploadState, record) => (
        <div className="mcap-cell">
          <div className="mcap-cell__main">
            <Space size={2} wrap>
              <UploadStateTag state={uploadState} />
              <ProcessingStateTag state={record.processing_state} />
            </Space>
          </div>
          <div className="mcap-cell__sub">
            <ReplicaTags locations={record.storage_locations} />
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 124,
      fixed: "right",
      render: (_, record) => {
        const isExternalStorage = !isReachable(record);
        return (
          <Space size="small">
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => onView(record)}
              />
            </Tooltip>
            <Tooltip
              title={
                isExternalStorage
                  ? "This file is stored on the organization's private storage server and cannot be downloaded from here."
                  : "Download"
              }
            >
              {/* span wrapper: AntD disabled buttons don't fire mouse events,
                  so the Tooltip needs an enabled element to attach to */}
              <span style={{ display: "inline-block" }}>
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  disabled={isExternalStorage}
                  onClick={() => onDownload(record)}
                />
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
};

export default createLogColumns;
